import { Button } from "@dmca/frontend/components/button";
import {
  Controller,
  NewPasswordFormFields,
} from "@dmca/frontend/components/input";
import { authClient } from "@dmca/frontend/hooks";
import { Container, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/settings/user/security"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { control, handleSubmit, setError, reset } = useForm({
    defaultValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
  });

  const mutation = useMutation({
    mutationKey: ["auth", "updatePassword"],
    mutationFn: async (data: { oldPassword: string; newPassword: string }) => {
      if (!data.oldPassword || !data.newPassword) {
        throw new Error("Both current and new passwords are required.");
      }
      const resp = await authClient.changePassword({
        currentPassword: data.oldPassword,
        newPassword: data.newPassword,
      });
      if (resp.error) {
        throw resp.error;
      }
      return resp.data;
    },
    onSuccess: () => {
      toast.success("Password updated successfully.");
      reset();
    },
    onError: (error) => {
      console.error("Error updating password:", error);
      if (error.message.includes("Invalid password")) {
        setError("oldPassword", {
          type: "manual",
          message: "Current password is incorrect.",
        });
      } else {
        console.error("Error updating password:", error);
        toast.error(
          "An unknown error occurred. Please try again or contact support."
        );
      }
    },
  });

  const onSubmit = handleSubmit((data) => {
    const { oldPassword, password } = data;
    mutation.mutate({
      oldPassword: oldPassword,
      newPassword: password,
    });
  });

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 1 }}
      component="form"
      onSubmit={onSubmit}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Update Password
      </Typography>
      <Controller
        control={control}
        name="oldPassword"
        rules={{
          required: "Current password is required.",
        }}
        inputProps={{
          label: "Current Password",
          type: "password",
        }}
      />
      <NewPasswordFormFields control={control} />
      <Button type="submit" loading={mutation.isPending}>
        Update Password
      </Button>
    </Container>
  );
}
